import {
  Breadcrumb,
  Button,
  PageHeader,
  Table,
  message,
  Popconfirm,
  Tag,
  Space,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Page from "_components/Page";
import { jobService } from "_services";
import Search from "./Search";
import { Auth } from "_helpers";

const List = ({ match }) => {
  const { path } = match;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchParams, setSearchParams] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    getItems(pageNumber, pageSize);
  }, []);

  const getItems = (pageNumber, pageSize) => {
    let paginationParams = searchParams.indexOf("?") === -1 ? "?" : "&";
    paginationParams += `pageNumber=${pageNumber}&pageSize=${pageSize}`;

    setLoading(true);
    jobService
      .search(`${searchParams}${paginationParams}`)
      .then((data) => {
        setItems(data.content);
        setLoading(false);
        setTotalRecords(data.pagination.totalRecords);
        setPageNumber(pageNumber);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleSearchResults = (results, total, searchParams) => {
    let totalResults = total ? total : totalRecords;
    setItems(results);
    setTotalRecords(totalResults);
    setSearchParams(searchParams);
    setPageNumber(1);
    setLoading(false);
  };

  const handlePaginateChange = (pageNumber, pageSize) => {
    getItems(pageNumber, pageSize);
  };

  const handleDelete = (id) => {
    jobService
      .delete(id)
      .then((x) => {
        message.success("Successfully deleted!");
        getItems(pageNumber, pageSize);
      })
      .catch((error) => {
        message.error(`${error}`);
      });
  };

  const getActionButtons = () => {
    let buttons = [];
    if (Auth.hasRole(Auth.getProfile(), ["SAU", "CAU"])) {
      buttons.push(
        <Link to={`${path}/add`} key="1">
          <Button>Add</Button>
        </Link>
      );
    }
    return buttons;
  };

  const getColumns = () => {
    const columns = [];
    columns.push({
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (text, record) => (
        <Link to={`/jobs/${record.id}/summary`}>{text}</Link>
      ),
    });
    columns.push({
      title: "Description",
      dataIndex: "description",
      key: "description",
    });
    columns.push({
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (text, record) => {
        return moment(text).format("YYYY-MM-DD");
      },
    });
    columns.push({
      title: "Request",
      key: "request",
      render: (text, record) => {
        const allRequestLines = record.workItems.map(
          (workItem) => workItem.requestLine
        );
        if (allRequestLines) {
          return (
            <span>
              {allRequestLines.map((requestLine, index) => (
                <Space key={index}>
                  <Link
                    to={`/material-requests/${requestLine.issueRequest.id}/summary`}
                    target="_blank"
                  >
                    {requestLine.issueRequest.requestNumber}
                  </Link>
                  <span>
                    (
                    {moment(requestLine.issueRequest.requestDate).format(
                      "YYYY-MM-DD"
                    )}
                    )
                  </span>
                </Space>
              ))}
            </span>
          );
        } else {
          return <span></span>;
        }
      },
    });
    columns.push({
      title: "Item Description",
      key: "requestItemDesc",
      render: (text, record) => {
        const allRequestLines = record.workItems.map(
          (workItem) => workItem.requestLine
        );
        if (allRequestLines) {
          return (
            <span>
              {allRequestLines.map((requestLine, index) => (
                <Space key={index}>{requestLine.stockType.name}</Space>
              ))}
            </span>
          );
        } else {
          return <span></span>;
        }
      },
    });
    columns.push({
      title: "Request Quantity",
      key: "requestQuantity",
      render: (text, record) => {
        const allRequestLines = record.workItems.map(
          (workItem) => workItem.requestLine
        );
        if (allRequestLines) {
          return (
            <span>
              {allRequestLines.map(
                (requestLine, index) => requestLine.totalItems
              )}
            </span>
          );
        } else {
          return <span></span>;
        }
      },
    });
    columns.push({
      title: "Status",
      dataIndex: "jobStatus",
      key: "jobStatus",
      render: (jobStatus) => {
        let color = "";
        if (["COMPLETE", "VERIFIED"].includes(jobStatus)) {
          color = "#87d068";
        }
        if (jobStatus === "OPEN") {
          color = "#2db7f5";
        }
        if (jobStatus === "VERIFICATION_INPROGRESS") {
          color = "#d4b106";
        }
        if (["IN_PROGRESS", "ACCEPTED"].includes(jobStatus)) {
          color = "#faad14";
        }
        return <Tag color={color}>{jobStatus}</Tag>;
      },
    });
    columns.push({
      title: "Action",
      key: "action",
      render: (text, record) =>
        Auth.hasRole(Auth.getProfile(), ["SAU", "CAU"]) && (
          <Popconfirm
            title="Are you sure to delete this item?"
            onConfirm={() => handleDelete(record.id)}
          >
            <CloseOutlined />
          </Popconfirm>
        ),
    });

    return columns;
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/jobs">Job Search</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <Search
            showSearchLoading={setLoading}
            onSearchResults={handleSearchResults}
          />
          <Table
            bordered
            loading={loading}
            rowKey="id"
            size="small"
            columns={getColumns()}
            dataSource={items}
            pagination={{
              current: pageNumber,
              pageSize: pageSize,
              total: totalRecords,
              showTotal: (total) => `Total ${total} items`,
              pageSizeOptions: [10, 20, 50, 100, 200],
              showSizeChanger: true,
              onChange: handlePaginateChange,
            }}
          />
        </Page>
      </PageHeader>
    </div>
  );
};

export default List;
